/**
 * 点击左上角灵力数值后的购买弹窗。
 *
 * 购买入口属于 HUD，不属于灵力流水页；微信原生页返回后也由这个常驻组件
 * 轮询服务端订单，客户端的 requestPayment success 绝不作为到账凭据。
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { describeFailure } from "../services/api-client";
import { requestMiniappPayment, type VirtualPaymentTicket } from "../services/host-bridge";
import {
  createTopupOrder,
  fetchPaymentOrder,
  fetchTopupTiers,
  newPaymentActionId,
  type TopupTiersResult,
} from "../services/payments-api";
import { LoadingState } from "./AsyncState";
import { Button, Sheet } from "./ui";

export function PowerTopupSheet({
  open,
  onOpen,
  onClose,
  onSucceeded,
}: {
  open: boolean;
  onOpen: () => void;
  onClose: () => void;
  onSucceeded: (result: {
    orderId: string;
    grantedPower: number;
    grantedBond: number;
    grantedAffinity: number;
    powerBalance: number | null;
  }) => void;
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const [tiers, setTiers] = useState<TopupTiersResult | null>(null);
  const [tiersFailure, setTiersFailure] = useState<string | null>(null);
  const [payingTier, setPayingTier] = useState<string | null>(null);
  const [paymentNotice, setPaymentNotice] = useState<string | null>(null);
  const [paymentFailure, setPaymentFailure] = useState<string | null>(null);
  const [pollVersion, setPollVersion] = useState(0);
  const [resumePayment, setResumePayment] = useState<({ orderId: string } & VirtualPaymentTicket) | null>(null);
  const [mockCheckout, setMockCheckout] = useState<{
    tier: TopupTiersResult["tiers"][number];
    actionId: string;
  } | null>(null);

  const loadTiers = useCallback(() => {
    const controller = new AbortController();
    setTiers(null);
    setTiersFailure(null);
    void fetchTopupTiers(controller.signal)
      .then(setTiers)
      .catch((cause) => {
        if (!controller.signal.aborted) setTiersFailure(describeFailure(cause).detail);
      });
    return () => controller.abort();
  }, []);

  // 每次打开都重读服务端档位；价格与赠送量不能使用上次打开留下的副本。
  useEffect(() => {
    if (!open) return;
    return loadTiers();
  }, [loadTiers, open]);

  const paymentReturn = useMemo(() => {
    const query = new URLSearchParams(location.search);
    const orderId = query.get("orderId") ?? "";
    if (!/^[0-9a-f-]{36}$/i.test(orderId)) return null;
    return { orderId, clientResult: query.get("paymentResult") ?? "unknown" };
  }, [location.search]);

  const clearPaymentReturn = useCallback(() => {
    const query = new URLSearchParams(location.search);
    for (const key of ["orderId", "actionId", "paymentResult"]) query.delete(key);
    const rest = query.toString();
    navigate({ pathname: location.pathname, search: rest ? `?${rest}` : "" }, { replace: true });
  }, [location.pathname, location.search, navigate]);

  useEffect(() => {
    if (!paymentReturn) return;
    let stopped = false;
    let timer: number | null = null;
    let attempts = 0;
    const controller = new AbortController();

    setPaymentFailure(null);
    setPaymentNotice(
      paymentReturn.clientResult === "success"
        ? "正在确认微信支付结果与灵力到账…"
        : "正在向服务端确认这笔订单的最终结果…",
    );

    const poll = async () => {
      attempts += 1;
      try {
        const order = await fetchPaymentOrder(paymentReturn.orderId, controller.signal);
        if (stopped) return;
        if (order.status === "fulfilled") {
          setResumePayment(null);
          setPaymentNotice(null);
          clearPaymentReturn();
          onSucceeded({
            orderId: order.id,
            grantedPower: order.grantedPower ?? 0,
            grantedBond: order.grantedBond,
            grantedAffinity: order.grantedAffinity,
            powerBalance: order.powerBalance,
          });
          return;
        }
        if (order.status === "closed" || order.status === "refunded") {
          setResumePayment(null);
          onOpen();
          setPaymentNotice(null);
          setPaymentFailure(order.status === "closed" ? "订单已关闭，没有发生到账。" : "订单已退款。");
          clearPaymentReturn();
          return;
        }
        if (order.resumePayment) {
          onOpen();
          setResumePayment({ orderId: order.id, ...order.resumePayment });
          setPaymentNotice("这笔订单已支付，请继续确认到账。");
          return;
        }
        if (attempts >= 80) {
          onOpen();
          setPaymentNotice("支付结果仍在确认中。可以重新查询，也可以稍后到「我的订单」查看。");
          return;
        }
        timer = window.setTimeout(() => void poll(), 1_500);
      } catch (cause) {
        if (stopped || controller.signal.aborted) return;
        if (attempts >= 80) {
          onOpen();
          setPaymentNotice(null);
          setPaymentFailure(describeFailure(cause).detail);
          return;
        }
        timer = window.setTimeout(() => void poll(), 1_500);
      }
    };
    void poll();

    return () => {
      stopped = true;
      controller.abort();
      if (timer !== null) window.clearTimeout(timer);
    };
  }, [clearPaymentReturn, onOpen, onSucceeded, paymentReturn, pollVersion]);

  const startTopup = useCallback(async (tierKey: string, actionId: string) => {
    setPayingTier(tierKey);
    setPaymentFailure(null);
    setPaymentNotice("正在生成微信支付订单…");
    try {
      const order = await createTopupOrder(tierKey, actionId);
      if (order.status === "fulfilled") {
        setMockCheckout(null);
        setPaymentNotice(null);
        onSucceeded({
          orderId: order.orderId,
          grantedPower: order.grantedPower,
          grantedBond: order.grantedBond,
          grantedAffinity: order.grantedAffinity,
          powerBalance: order.powerBalance,
        });
        return;
      }
      if (!order.payment) throw new Error("支付参数缺失");
      const started = await requestMiniappPayment({
        orderId: order.orderId,
        actionId: order.actionId,
        // 从哪个页面点开灵力，就回到哪个页面；购买弹窗由 AppShell 统一恢复。
        redirect: location.pathname,
        ...order.payment,
      });
      if (!started) {
        setPaymentNotice(null);
        setPaymentFailure("当前小程序壳无法打开原生支付页，请重新进入小程序后再试。");
      }
    } catch (cause) {
      setPaymentNotice(null);
      setPaymentFailure(describeFailure(cause).detail);
    } finally {
      setPayingTier(null);
    }
  }, [location.pathname, onSucceeded]);

  const chooseTier = useCallback((tier: TopupTiersResult["tiers"][number]) => {
    const actionId = newPaymentActionId();
    if (tiers?.paymentMode === "development_stub") {
      setPaymentFailure(null);
      setPaymentNotice(null);
      setMockCheckout({ tier, actionId });
      return;
    }
    void startTopup(tier.key, actionId);
  }, [startTopup, tiers?.paymentMode]);

  return (
    <>
      <Sheet open={open} title="关于灵力" onClose={onClose}>
        <div className="prose">
          <p>
            每天首次进入时，若灵力少于20，会赠送20灵力。灵力决定缈缈现在
            能做多少事。和你聊天会消耗一点，画画这类费力的事消耗得多一些。
          </p>
          <p>
            灵力用完了她<strong>不会消失</strong>，会变成
            <strong>笨笨缈缈</strong>——反应短一些、迷糊一些，但不消耗灵力，
            陪伴不会中断。两个形态共享同一份记忆，羁绊与好感照常累积。
          </p>
        </div>

        {!tiers && !tiersFailure ? <LoadingState label="正在读取充值档位" /> : null}

        {tiersFailure ? (
          <div className="topup-status is-error" role="alert">
            <p>{tiersFailure}</p>
            <Button tone="quiet" onClick={loadTiers}>重新读取</Button>
          </div>
        ) : null}

        {tiers?.available ? (
          <div
            className="topup-grid"
            aria-label="购买灵力"
            data-payment-mode={tiers.paymentMode}
          >
            {tiers.tiers.map((tier) => (
              <button
                key={tier.key}
                type="button"
                className="topup-card"
                disabled={payingTier !== null}
                onClick={() => chooseTier(tier)}
              >
                <span className="topup-card__price">{formatCny(tier.priceCents)}</span>
                <span className="topup-card__power">{tier.totalPower} 灵力</span>
                <span className="topup-card__relationship">
                  好感 +{tier.grantedAffinity} · 羁绊 +{tier.grantedBond}
                </span>
                {tier.bonusPower > 0 ? (
                  <span className="topup-card__bonus">含赠送 {tier.bonusPower}</span>
                ) : null}
                <span className="topup-card__action">
                  {payingTier === tier.key ? "正在下单" : "购买"}
                </span>
              </button>
            ))}
          </div>
        ) : null}

        {paymentNotice ? <p className="topup-status" role="status">{paymentNotice}</p> : null}
        {paymentReturn && !resumePayment ? (
          <Button tone="quiet" onClick={() => setPollVersion((value) => value + 1)}>重新查询订单</Button>
        ) : null}
        {resumePayment ? (
          <Button block onClick={() => {
            // 点击时重新取票据，避免用户停留超过短期票据有效期。
            void fetchPaymentOrder(resumePayment.orderId).then(async (order) => {
              if (!order.resumePayment) {
                setResumePayment(null);
                setPollVersion((value) => value + 1);
                return;
              }
              const started = await requestMiniappPayment({
                ...order.resumePayment, orderId: order.id, actionId: `resume-${order.id}`, redirect: location.pathname,
              });
              if (!started) setPaymentFailure("请重新进入小程序后继续确认到账。");
            }).catch((cause) => setPaymentFailure(describeFailure(cause).detail));
          }}>继续确认到账</Button>
        ) : null}
        {paymentFailure && !mockCheckout ? (
          <p className="topup-status is-error" role="alert">{paymentFailure}</p>
        ) : null}
      </Sheet>

      <Sheet
        open={mockCheckout !== null}
        title="微信支付 · 测试模拟"
        onClose={() => {
          if (!payingTier) setMockCheckout(null);
        }}
      >
        {mockCheckout ? (
          <div className="wechat-pay-mock">
            <div className="wechat-pay-mock__brand" aria-hidden>
              <span>✓</span>
              微信支付
            </div>
            <p className="wechat-pay-mock__merchant">Dreamverse 灵力</p>
            <strong className="wechat-pay-mock__amount">
              {formatCny(mockCheckout.tier.priceCents)}
            </strong>
            <p className="wechat-pay-mock__detail">
              购买 {mockCheckout.tier.totalPower} 灵力
              {mockCheckout.tier.bonusPower > 0
                ? `（含赠送 ${mockCheckout.tier.bonusPower}）`
                : ""}
            </p>
            <p className="wechat-pay-mock__hint">
              当前是测试环境。点击确认后，服务端测试支付桩会自动返回成功。
            </p>
            {paymentFailure ? (
              <p className="topup-status is-error" role="alert">{paymentFailure}</p>
            ) : null}
            <Button
              block
              className="wechat-pay-mock__confirm"
              disabled={payingTier !== null}
              onClick={() => void startTopup(mockCheckout.tier.key, mockCheckout.actionId)}
            >
              {payingTier ? "正在支付…" : "确认支付"}
            </Button>
            <Button
              tone="quiet"
              block
              disabled={payingTier !== null}
              onClick={() => setMockCheckout(null)}
            >
              取消
            </Button>
          </div>
        ) : null}
      </Sheet>
    </>
  );
}

function formatCny(cents: number): string {
  return new Intl.NumberFormat("zh-CN", {
    style: "currency",
    currency: "CNY",
    minimumFractionDigits: 2,
  }).format(cents / 100);
}
